import * as mutation from "./mutation-types";
import * as action from "./action-types";
import * as lcMutation from "@/store/levy-collections/mutation-types";
import * as lcpMutation from "@/store/levy-collection-providers/mutation-types";
import * as estimatorMutation from "@/store/levy-estimator/mutation-types";
import * as invoiceMutation from "@/store/invoices/mutation-types";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import * as systemLogMutation from "@/store/system-logs/mutation-types";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";
import { GLOBAL_ERROR } from "@/store/system/mutation-types";
import { deepCopyAxiosException } from "@/store/helpers";
import { tableHelper } from "@/helpers/table.helper";
import axios from "axios";
import store from "@/store";

export const actions = {
  async [action.LIST]({ commit, state }) {
    commit(mutation.LIST_STARTED);
    try {
      const url = tableHelper.constructUrl(
        `/api/levy-collections/${state.listing.levyCollectionId}/overrides`,
        state.listing.paging
      );
      const { headers, data } = await axios.get(url);
      commit(mutation.LIST_SUCCEEDED, data);
      commit(mutation.LIST_SET_CRITERIA_DESCRIPTION, tableHelper.getCriteriaInfo(headers));
      commit(mutation.LIST_SET_PAGING, tableHelper.getPagingFromHeaders(headers));
      return true;
    } catch (e) {
      commit(mutation.LIST_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.LIST_NEXT_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page + 1 });
    await dispatch(action.LIST);
  },
  async [action.LIST_PREVIOUS_PAGE]({ state, commit, dispatch }) {
    commit(mutation.LIST_SET_PAGING, { page: state.listing.paging.page - 1 });
    await dispatch(action.LIST);
  },

  async [action.EDIT_GET]({ commit }, { levyCollectionId, levyCollectionOverrideId }) {
    commit(mutation.EDIT_GET_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/overrides/${levyCollectionOverrideId}`;
      const { data } = await axios.get(url);
      commit(mutation.EDIT_GET_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.EDIT_GET_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.EDIT_SAVE]({ commit, state, dispatch }, levyCollectionId) {
    commit(mutation.EDIT_SAVE_STARTED);
    try {
      let url = `/api/levy-collections/${levyCollectionId}/overrides`;
      if (!state.edit.isNew) {
        await axios.put(`${url}/${state.edit.data.id}`, state.edit.data);
      } else {
        await axios.post(url, state.edit.data);
      }
      await dispatch(lcpAction.EXISTS, { levyCollectionId, providerId: state.edit.data.providerId });
      commit(systemLogMutation.STATE_UNLOAD);
      commit(lcMutation.STATE_UNLOAD);
      commit(invoiceMutation.STATE_UNLOAD);
      commit(estimatorMutation.STATE_UNLOAD);
      commit(mutation.STATE_UNLOAD);
      const onlyUnloadListing = !store.state.levyCollectionProviders.exists.exists;
      commit(lcpMutation.STATE_UNLOAD, onlyUnloadListing);
      commit(mutation.EDIT_SAVE_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.EDIT_SAVE_FAILED, deepCopyAxiosException(e));
      return false;
    }
  },
  async [action.EDIT_DELETE]({ commit, state, dispatch }, { levyCollectionId, levyCollectionOverrideId }) {
    commit(mutation.EDIT_DELETE_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/overrides/${levyCollectionOverrideId}`;
      await axios.delete(url);
      await dispatch(lcpAction.EXISTS, { levyCollectionId, providerId: state.edit.data.providerId });
      commit(systemLogMutation.STATE_UNLOAD);
      commit(lcMutation.STATE_UNLOAD);
      commit(invoiceMutation.STATE_UNLOAD);
      commit(estimatorMutation.STATE_UNLOAD);
      commit(mutation.STATE_UNLOAD);
      const onlyUnloadListing = !store.state.levyCollectionProviders.exists.exists;
      commit(lcpMutation.STATE_UNLOAD, onlyUnloadListing);
      commit(mutation.EDIT_DELETE_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.EDIT_DELETE_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.GET_CANDIDATES_FOR_COPYING]({ commit }, levyCollectionId) {
    commit(mutation.GET_CANDIDATES_FOR_COPYING_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/overrides/candidates-for-copying`;
      const { data } = await axios.get(url);
      commit(mutation.GET_CANDIDATES_FOR_COPYING_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.GET_CANDIDATES_FOR_COPYING_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.GET_ALL]({ commit }, levyCollectionId) {
    commit(mutation.GET_ALL_STARTED, levyCollectionId);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/overrides/all`;
      const { data } = await axios.get(url);
      commit(mutation.GET_ALL_SUCCEEDED, data);
      return true;
    } catch (e) {
      commit(mutation.GET_ALL_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.COPY]({ commit }, { levyCollectionId, overrideIds }) {
    commit(mutation.COPY_STARTED);
    try {
      const url = `/api/levy-collections/${levyCollectionId}/overrides/copy`;
      await axios.post(url, { overrideIds });
      commit(systemLogMutation.STATE_UNLOAD);
      commit(lcMutation.STATE_UNLOAD);
      commit(lcpMutation.STATE_UNLOAD);
      commit(invoiceMutation.STATE_UNLOAD);
      commit(mutation.STATE_UNLOAD);
      commit(mutation.COPY_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.COPY_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  },

  async [action.EXPORT]({ commit, state }) {
    commit(mutation.EXPORT_STARTED);
    try {
      commit(SHOW_NOTIFICATION, "Your export will commence shortly.");
      const url = tableHelper.constructUrl(
        `/api/exports/levy-collections/${state.listing.levyCollectionId}/overrides`,
        state.listing.criteria
      );
      tableHelper.downloadExportedSpreadsheet(url);
      commit(mutation.EXPORT_SUCCEEDED);
      return true;
    } catch (e) {
      commit(mutation.EXPORT_FAILED);
      commit(GLOBAL_ERROR, deepCopyAxiosException(e));
      return false;
    }
  }
};
